import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";

export type DropdownOption<T extends string> = {
  value: T;
  label: string | ReactNode;
  shortLabel?: string | ReactNode;
  isDisabled?: boolean;
  group?: string;
};

export function Dropdown<T extends string>(props: {
  value: T;
  options: Array<DropdownOption<T>>;
  onChange: (v: T) => void;
  ariaLabel: string;
  widthPx?: number;
}) {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const label = useMemo(() => {
    const option = props.options.find((o) => o.value === props.value);
    return option?.shortLabel ?? option?.label ?? props.value;
  }, [props.options, props.value]);

  // Initialize: only expand flat groups (like "Suggested") by default, keep hierarchical groups collapsed
  useEffect(() => {
    const groups = new Set<string>();
    props.options.forEach((o) => {
      if (o.group && !o.group.includes(" / ")) {
        // Flat groups (e.g., "Suggested") are expanded by default
        groups.add(o.group);
      }
    });
    setExpandedGroups(groups);
  }, [props.options]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const el = wrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div ref={wrapRef} className="ddWrap" style={{ width: props.widthPx ? `${props.widthPx}px` : undefined }}>
      <button
        type="button"
        className="ddBtn"
        aria-label={props.ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <span className="ddLabel">{label}</span>
        <span className="ddCaret">▼</span>
      </button>

      {open ? (
        <div className="ddMenu" role="listbox" aria-label={props.ariaLabel}>
          {(() => {
            let currentMainGroup: string | undefined;
            let currentSubGroup: string | undefined;

            return props.options.map((o) => {
              const active = o.value === props.value;
              const disabled = o.isDisabled;

              // Parse hierarchical group: "Main Group / Sub Group"
              let mainGroup = o.group;
              let subGroup: string | undefined;

              if (o.group?.includes(" / ")) {
                const parts = o.group.split(" / ");
                mainGroup = parts[0];
                subGroup = parts[1];
              }

              // Determine which headers to show
              const newMainGroup = mainGroup && mainGroup !== currentMainGroup;
              // Sub-header shows when sub-group changes — including on the first item of a new main group
              const newSubGroup = subGroup && (subGroup !== currentSubGroup || newMainGroup);

              const isMainGroupExpanded = mainGroup && expandedGroups.has(mainGroup);
              const isSubGroupExpanded = !subGroup || expandedGroups.has(subGroup);
              const shouldShowItem = !o.group || (isMainGroupExpanded && isSubGroupExpanded) || disabled;

              // Update tracking after computing flags
              if (newMainGroup) {
                currentMainGroup = mainGroup;
                currentSubGroup = undefined;
              }
              if (newSubGroup) {
                currentSubGroup = subGroup;
              }

              const elements: React.ReactNode[] = [];

              // Main group header
              if (newMainGroup && mainGroup) {
                elements.push(
                  <div
                    key={`group-${mainGroup}`}
                    className={`ddGroupHeader ${isMainGroupExpanded ? "expanded" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newGroups = new Set(expandedGroups);
                      if (newGroups.has(mainGroup!)) {
                        newGroups.delete(mainGroup!);
                      } else {
                        newGroups.add(mainGroup!);
                      }
                      setExpandedGroups(newGroups);
                    }}
                  >
                    <span className="ddGroupToggle">▶</span>
                    {mainGroup}
                  </div>
                );
              }

              // Sub-group header (only show when main group is expanded)
              if (newSubGroup && subGroup && isMainGroupExpanded) {
                elements.push(
                  <div
                    key={`subgroup-${subGroup}`}
                    className={`ddSubGroupHeader ${isSubGroupExpanded ? "expanded" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newGroups = new Set(expandedGroups);
                      if (newGroups.has(subGroup!)) {
                        newGroups.delete(subGroup!);
                      } else {
                        newGroups.add(subGroup!);
                      }
                      setExpandedGroups(newGroups);
                    }}
                  >
                    <span className="ddGroupToggle">▶</span>
                    {subGroup}
                  </div>
                );
              }

              // Item
              if (shouldShowItem) {
                elements.push(
                  <div
                    key={`item-${o.value}`}
                    role={disabled ? undefined : "option"}
                    aria-selected={disabled ? undefined : active}
                    className={`ddItem ${active ? "active" : ""} ${disabled ? "disabled" : ""}`}
                    onClick={(e) => {
                      if (disabled) return;
                      e.preventDefault();
                      e.stopPropagation();
                      props.onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    {o.label}
                  </div>
                );
              }

              return elements.length > 0 ? (
                <React.Fragment key={o.value}>{elements}</React.Fragment>
              ) : null;
            });
          })()}
        </div>
      ) : null}
    </div>
  );
}
